// Lista de problemas com o estado de cada um para esta equipe.

import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { ProblemListItemDto, SubmissionListItemDto } from "@compila/shared";
import { api } from "../../lib/api.js";
import { ErrorBox, Spinner } from "../../components/ui.js";
import { formatMemory, formatTime } from "../../lib/format.js";

type Estado = "resolvido" | "pendente" | "erro" | "nao-tentado";

export function TeamProblemsPage(): React.JSX.Element {
  const problems = useQuery({
    queryKey: ["problems"],
    queryFn: () => api.get<ProblemListItemDto[]>("/problems"),
  });

  const submissions = useQuery({
    queryKey: ["submissions"],
    queryFn: () => api.get<SubmissionListItemDto[]>("/submissions"),
  });

  if (problems.isPending) return <Spinner />;
  if (problems.error) return <ErrorBox message={(problems.error as Error).message} />;

  if (problems.data.length === 0) {
    return (
      <p className="rounded border border-slate-200 bg-white p-8 text-center text-slate-500">
        Nenhum problema liberado ainda. Aguarde o inicio da prova.
      </p>
    );
  }

  const porProblema = agrupar(submissions.data ?? []);

  return (
    <div className="space-y-3">
      {problems.data.map((p) => {
        const info = porProblema.get(p.id);
        const estado = info?.estado ?? "nao-tentado";
        return (
          <Link
            key={p.id}
            to={`/problemas/${p.id}`}
            className="flex items-center gap-4 rounded-lg border border-slate-200 bg-white p-4 transition-shadow hover:shadow-md"
          >
            <span
              className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg text-xl font-bold ${corDaLetra(estado)}`}
            >
              {p.label}
            </span>

            <div className="min-w-0 flex-1">
              <h2 className="truncate font-semibold text-slate-900">{p.title}</h2>
              <p className="text-xs text-slate-500">
                Limite: {formatTime(p.timeLimitMs)} · {formatMemory(p.memoryLimitKb)}
              </p>
            </div>

            <div className="text-right text-sm">
              <EstadoRotulo estado={estado} tentativas={info?.tentativas ?? 0} />
            </div>
          </Link>
        );
      })}
    </div>
  );
}

function EstadoRotulo({ estado, tentativas }: { estado: Estado; tentativas: number }) {
  if (estado === "resolvido") {
    return <span className="font-semibold text-emerald-700">Resolvido</span>;
  }
  if (estado === "pendente") {
    return <span className="animate-pulse font-semibold text-amber-700">Julgando</span>;
  }
  if (estado === "erro") {
    return (
      <span className="font-semibold text-rose-700">
        {tentativas} {tentativas === 1 ? "tentativa" : "tentativas"}
      </span>
    );
  }
  return <span className="text-slate-400">Nao tentado</span>;
}

function corDaLetra(estado: Estado): string {
  switch (estado) {
    case "resolvido":
      return "bg-emerald-500 text-white";
    case "pendente":
      return "bg-amber-400 text-amber-950";
    case "erro":
      return "bg-rose-500 text-white";
    default:
      return "bg-slate-200 text-slate-600";
  }
}

/**
 * Estado por problema a partir do historico da propria equipe.
 *
 * Resolvido vence tudo; depois pendente; depois erro. Um CE nao conta como
 * tentativa aqui, para a contagem bater com a penalidade do placar.
 */
function agrupar(
  submissions: SubmissionListItemDto[],
): Map<string, { estado: Estado; tentativas: number }> {
  const mapa = new Map<string, { estado: Estado; tentativas: number }>();

  for (const s of submissions) {
    const atual = mapa.get(s.problemId) ?? { estado: "nao-tentado" as Estado, tentativas: 0 };

    if (s.verdict === "AC") {
      atual.estado = "resolvido";
    } else if (
      atual.estado !== "resolvido" &&
      (s.status === "QUEUED" || s.status === "JUDGING")
    ) {
      atual.estado = "pendente";
    } else if (
      atual.estado !== "resolvido" &&
      atual.estado !== "pendente" &&
      s.status === "DONE" &&
      s.verdict !== "CE"
    ) {
      atual.estado = "erro";
    }

    if (s.status === "DONE" && s.verdict !== "CE" && s.verdict !== "AC") {
      atual.tentativas += 1;
    }

    mapa.set(s.problemId, atual);
  }

  return mapa;
}
